import SearchIcon from "@mui/icons-material/Search";
import { Box, Grid, Stack, Typography } from "@mui/material";
import moment from "moment";
import React, { useEffect, useState } from "react";
import AppLoader from "../../../components/AppLoader";
import PaperContainer from "../../../components/Containers/PaperContainer";
import GlobalButton from "../../../components/CustomButton";
import CustomRangePicker from "../../../components/DateRangePicker";
import { CURRENCY } from "../../../constants/Variables";
import { getAdminReportData } from "../../../services/dashboardApiFn";
import { handleOpenSnackBar, roundTo2Decimal } from "../../../utils";
import NavigationDrawer from "./../../../components/NavigationDrawer/index";
import OrderReportChart from "./OrderReportChart";
import ReportCard from "./ReportCard";
import ReportChart from "./ReportChart";
import ReportPiewChart from "./ReportPiewChart";
import MothlySubscriptionReport from "./SubscriptionReport";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

let months = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function AdminReports() {
  const { t } = useTranslation(["report"]);
  const [isLoading, setIsLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [appointmentChartData, setAppointmentChartData] = useState([]);
  const [monthlyReport, setMonthlyReport] = useState([]);
  const [subscriptionHistory, setSubcriptionHistory] = useState([]);
  const [monthlySubscription, setMonthlySubscription] = useState([]);
  const [startDate, setStartDate] = useState(moment().subtract(1, "month"));
  const [endDate, setEndDate] = useState(moment());
  const userData = useSelector((state) => state.userData.user);
  const id = userData._id;

  useEffect(() => {
    if (startDate && endDate) {
      fetchReportData(startDate, endDate);
    }
  }, []);

  const fetchReportData = async (startDate, endDate) => {
    let query = {
      startDate,
      endDate,
    };
    try {
      setIsLoading(true);
      const response = await getAdminReportData(id, query);
      if (response.data.status === "success") {
        const data = response.data.data;
        const apChartData = data?.companyDailyEarning?.map((val, ind) => {
          return {
            orders: val.totalAppointments,
            earnings: val.totalEarnings,
            date: new Date(val._id).getTime(),
          };
        });
        const finalOrderReport = data.monthlyOrderReport.map((val, ind) => {
          return {
            month: months[val._id.month - 1],
            totalOrders: val.totalOrders,
            earning: val.totalEarning,
          };
        });
        const subscriptionReport = data.subscriptionSummary.map((val, ind) => {
          return {
            value: val.planCount,
            category: val.planName,
            userType: val.userType,
          };
        });
        const monthlySubscriptionReport = data.monthlySubscriptions.map(
          (val, ind) => {
            return {
              month: months[val.month - 1],
              totalOrders: val.subscriptionCount,
              earning: val.totalEarnings,
              userType: val.userType,
            };
          }
        );
        setReportData(data);
        setAppointmentChartData(apChartData);
        setMonthlyReport(finalOrderReport);
        setSubcriptionHistory(subscriptionReport);
        setMonthlySubscription(monthlySubscriptionReport);
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const totalEcommerceEarnings =
    reportData?.adminEcomerceEarning +
    reportData?.totalEarningsFromEcommerce +
    reportData?.platformEarningsFromOrders;

  const ecommerceReportData = [
    {
      primary: "adminCommission",
      secondary: "profit",
      value: `${roundTo2Decimal(
        reportData?.adminEcomerceEarning || 0
      )} ${CURRENCY}`,
    },
    {
      primary: "earningsAsSeller",
      secondary: "profit",
      value: `${roundTo2Decimal(
        reportData?.totalEarningsFromEcommerce || 0
      )} ${CURRENCY}`,
    },
    {
      primary: "platformEarning",
      secondary: "profit",
      value: `${roundTo2Decimal(
        reportData?.platformEarningsFromOrders || 0
      )} ${CURRENCY}`,
    },
    {
      primary: "totalEarnings",
      secondary: "profit",
      value: `${roundTo2Decimal(totalEcommerceEarnings || 0)} ${CURRENCY}`,
    },
  ];

  const totalServicesEarnings =
    reportData?.servicesEarnings[0]?.cleaning ||
    0 + reportData?.servicesEarnings[0]?.carServices ||
    0 + reportData?.platformEarningsFromService ||
    0;

  const servicesReportData = [
    {
      primary: "cleaningServices",
      secondary: "profit",
        value: `${roundTo2Decimal(
        reportData?.servicesEarnings[0]?.cleaning || 0
      )} ${CURRENCY}`,
    },
    {
      primary: "carServices",
      secondary: "profit",
      value: `${roundTo2Decimal(
        reportData?.servicesEarnings[0]?.carServices || 0
      )} ${CURRENCY}`,
    },
    {
      primary: "platformEarning",
      secondary: "profit",
      value: `${roundTo2Decimal(
        reportData?.platformEarningsFromService || 0
      )} ${CURRENCY}`,
    },
    {
      primary: "totalEarnings",
      secondary: "profit",
      value: `${roundTo2Decimal(totalServicesEarnings)} ${CURRENCY}`,
    },
  ];

  const totalPlatformEarnings =
    reportData?.platformEarningsFromOrders ||
    0 + reportData?.platformEarningsFromService ||
    0;

  const platformEarningsReportData = [
    {
      primary: "platformEarningFromEcommerce",
      secondary: "profit",
      value: `${roundTo2Decimal(
        reportData?.platformEarningsFromOrders || 0
      )} ${CURRENCY}`,
    },
    {
      primary: "platformEarningFromServices",
      secondary: "profit",
      value: `${roundTo2Decimal(
        reportData?.platformEarningsFromService || 0
      )} ${CURRENCY}`,
    },
    {
      primary: "totalEarnings",
      secondary: "profit",
      value: `${roundTo2Decimal(totalPlatformEarnings)} ${CURRENCY}`,
    },
  ];

  const subscriptionEarningsReportData = [
    {
      primary: "purchasedSubscriptions",
      secondary: "allSubscriptions",
      value: roundTo2Decimal(reportData?.totalPurchasedSubscription || 0),
    },
    {
      primary: "subscriptionsEarnings",
      secondary: "profit",
        value: `${roundTo2Decimal(
        reportData?.subscriptionEarnings || 0
      )} ${CURRENCY}`,
    },
  ];

  const ordersAndAppointmentsReportData = [
    {
      primary: "totalAppointments",
      secondary: "allAppointments",
      value: reportData?.totalAppointments || 0,
    },
    {
      primary: "completedAppointments",
      secondary: "allCompletedAppointments",
      value: reportData?.completedAppointments || 0,
    },
    {
      primary: "totalOrders",
      secondary: "allOrders",
      value: reportData?.totalOrders || 0,
    },
  ];

  const renderHeaderAction = () => {
    return (
      <Stack
        gap={2}
        direction={"row"}
        alignItems={"center"}
        flex={1}
        justifyContent={"flex-end"}
      >
        <Stack direction={"row"} alignItems={"center"}>
          <CustomRangePicker
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            stackProps={{
              borderRadius: "6px 0px 0px 6px",
              border: "1px solid #dddddd",
            }}
          />
          <GlobalButton
            endIcon={<SearchIcon />}
            handleClickBtn={() => {
              fetchReportData(startDate, endDate);
            }}
            styleProps={{
              color: "primary",
              width: "auto",
              borderRadius: "0px 6px 6px 0px",
              minWidth: 45,
              height: "42px",
              "& .MuiButton-icon": {
                marginLeft: "0px",
              },
            }}
          />
        </Stack>
      </Stack>
    );
  };

  return (
    <NavigationDrawer>
      <AppLoader isVisible={isLoading} />
      <PaperContainer
        title={t("reportingAndAnalytics")}
        subtitle={t("accessAllReports")}
        headerActions={renderHeaderAction()}
      >
        <Box mb={2}>
          <Typography variant="h6" fontWeight={600} mb={1}>
            {t("ecommerceReport")}
          </Typography>
          <Grid container spacing={2}>
            {ecommerceReportData.map((val, index) => (
              <Grid item xs={12} md={3} key={index}>
                <ReportCard data={val} />
              </Grid>
            ))}
          </Grid>
        </Box>

        <Box mb={2}>
          <Typography variant="h6" fontWeight={600} mb={1}>
            {t("servicesEarningsReport")}
          </Typography>
          <Grid container spacing={2}>
            {servicesReportData.map((val, index) => (
              <Grid item xs={12} md={3} key={index}>
                <ReportCard data={val} />
              </Grid>
            ))}
          </Grid>
        </Box>

        <Box mb={2}>
          <Typography variant="h6" fontWeight={600} mb={1}>
            {t("platformEarningsReport")}
          </Typography>
          <Grid container spacing={2}>
            {platformEarningsReportData.map((val, index) => (
              <Grid item xs={12} md={4} key={index}>
                <ReportCard data={val} />
              </Grid>
            ))}
          </Grid>
        </Box>

        <Box mb={2}>
          <Typography variant="h6" fontWeight={600} mb={1}>
            {t("subscriptionEarningsReport")}
          </Typography>
          <Grid container spacing={2}>
            {subscriptionEarningsReportData.map((val, index) => (
              <Grid item xs={12} md={6} key={index}>
                <ReportCard data={val} />
              </Grid>
            ))}
          </Grid>
        </Box>

        <Box mb={2}>
          <Typography variant="h6" fontWeight={600} mb={1}>
            {t("ordersAndAppointments")}
          </Typography>
          <Grid container spacing={2}>
            {ordersAndAppointmentsReportData.map((val, index) => (
              <Grid item xs={12} md={4} key={index}>
                <ReportCard data={val} />
              </Grid>
            ))}
          </Grid>
        </Box>

        <Grid container mt={3} spacing={1}>
          <Grid item xs={12}>
            <ReportChart
              chartData={appointmentChartData}
              isLoading={isLoading}
            />
          </Grid>
          <Grid item xs={12}>
            <OrderReportChart chartData={monthlyReport} isLoading={isLoading} />
          </Grid>
          <Grid item xs={12} md={8}>
            <MothlySubscriptionReport chartData={monthlySubscription} />
          </Grid>
          <Grid item xs={12} md={4}>
            <ReportPiewChart
              chartData={subscriptionHistory}
              isLoading={false}
            />
          </Grid>
        </Grid>
      </PaperContainer>
    </NavigationDrawer>
  );
}

export default AdminReports;
